import { ref } from '@vue/composition-api'
import router from '@/shared/router'
import { storage, firestore } from '@/shared/services/firebase'
import {
  fetchDocumentRequest, createOrUpdateDocumentRequest, updateUser, markAsReadUserMessages,
} from '@/shared/services/documentRequestService'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@hw-ui/components/toastification/ToastificationContent.vue'

export default function useDocumentRequestView() {
  // Use toast
  const toast = useToast()
  const documentRequestData = ref(null)

  const getLinkAttachment = async path => {
    try {
      const url = await storage.ref(`documentRequests/${path}`)
        .getDownloadURL()
      return url
    } catch (error) {
      return ''
    }
  }

  const refetchData = () => {
    // refetch data
    fetchDocumentRequest(router.currentRoute.params.id)
      .then(async res => {
        documentRequestData.value = null
        const requestedDocuments = await Promise.all(
          res.requestedDocuments.map(async document => {
            if (document.attachments && document.attachments.length > 0) {
              const attachments = await Promise.all(document.attachments.map(async attachment => ({
                name: attachment,
                link: await getLinkAttachment(`${res.id}/${attachment}`),
              })))
              return {
                ...document,
                attachments,
                canEdit: !document.submitted,
              }
            }

            return {
              ...document,
              canEdit: !document.submitted,
            }
          }),
        )
        const data = {
          ...res,
          requestedDocuments,
        }
        documentRequestData.value = data
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error fetching document request',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        router.push({ name: 'apps-document-requests-list' })
      })
  }

  const updateDocumentRequested = async newState => {
    const documents = newState.map(value => {
      let attachments = []
      if (value.attachments && value.attachments.length) {
        attachments = value.attachments.map(item => item.name)
      }
      return { ...value, attachments }
    })
    const data = {
      id: documentRequestData.value.id,
      requestedDocuments: documents,
      status: newState.every(doc => doc.submitted === true) ? 'closed' : 'open',
    }
    createOrUpdateDocumentRequest({ data })
      .then(() => {
        if (data.status === 'closed') {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Document request closed',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          router.back()
        } else {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Document request updated',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        }
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error updating document request',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .finally(() => {
        refetchData()
      })
  }

  const updateAttachmentRequested = newState => {
    const data = {
      id: documentRequestData.value.id,
      attachments: newState,
    }
    createOrUpdateDocumentRequest({ data })
      .then(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Document request updated',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error updating document request',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .finally(() => {
        refetchData()
      })
  }

  const addComment = newComment => {
    const data = {
      id: documentRequestData.value.id,
      comments: firestore.FieldValue.arrayUnion(newComment),
      badgeUser: firestore.FieldValue.increment(1),
    }

    createOrUpdateDocumentRequest({ data })
      .then(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Document request updated',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error updating document request',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .finally(() => {
        refetchData()
      })
  }

  fetchDocumentRequest(router.currentRoute.params.id)
    .then(async res => {
      const requestedDocuments = await Promise.all(
        res.requestedDocuments.map(async document => {
          if (document.attachments && document.attachments.length > 0) {
            const attachments = await Promise.all(document.attachments.map(async attachment => ({
              name: attachment,
              link: await getLinkAttachment(`${res.id}/${attachment}`),
            })))
            return {
              ...document,
              attachments,
              canEdit: !document.submitted,
            }
          }

          return {
            ...document,
            canEdit: !document.submitted,
          }
        }),
      )

      const data = {
        ...res,
        requestedDocuments,
      }
      documentRequestData.value = data
    })
    .catch(() => {
      toast({
        component: ToastificationContent,
        props: {
          title: 'Error fetching document request',
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
      router.push({ name: 'apps-document-requests-list' })
    })

  const updateUserRequested = newState => {
    const data = {
      id: documentRequestData.value.user.id,
      ...newState,
    }
    try {
      updateUser({ data })
    } catch (e) {
      toast({
        component: ToastificationContent,
        props: {
          title: 'Error updating request',
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
    }
  }

  markAsReadUserMessages(router.currentRoute.params.id)

  return {
    documentRequestData,
    updateDocumentRequested,
    updateAttachmentRequested,
    updateUserRequested,
    addComment,
  }
}
